import {useApolloClient} from '@apollo/client';
import {useResetRawTransactionMutation} from '@transactions/graphql';
import Money from 'common/Money';
import {ChevronRight} from 'lucide-react';
import * as React from 'react';
import {AmountDisplay} from 'src/AmountDisplay';
import evictAccountRawTransactions from 'src/apolloCache/evictAccountRawTransactions';
import {Button} from 'src/Button';
import {ConsumersList} from 'src/ConsumersList';
import Display from 'src/DetailComponents/RawTransaction/Display';
import Section from 'src/DetailComponents/Section';
import JsonDisplay from 'src/JsonDisplay';
import {useModals} from 'src/Modals';
import {TRANSFER_TYPE_LABELS} from 'src/TransferKindSelect';
import {Container, DetailHeader, destructiveOutlineClass} from '..';
import type {FullRawTransactionFragment} from '@transactions/graphql';

type Props = {
  rawTransaction: FullRawTransactionFragment;
  /** Called after the raw transaction is deleted (e.g. to navigate away) */
  onDelete?: () => void;
};

const RawTransactionDetail = (props: Props) => {
  const {rawTransaction, onDelete} = props;
  const modals = useModals();
  const apolloClient = useApolloClient();
  const isConsumed =
    rawTransaction.remainingAmount !== null &&
    rawTransaction.remainingAmount !== rawTransaction.amount;
  // Mirrors the server-side gates in deleteRawTransactions: a consumed or
  // reconciled line can't be deleted, so Delete isn't offered for one.
  const deleteBlockedReason = isConsumed
    ? 'consumed'
    : rawTransaction.reconciliation
      ? 'reconciled'
      : null;

  // The consuming transactions are deleted server-side, so refetch this raw
  // transaction to refresh its consumers list and remainingAmount.
  const [resetRawTransaction, {loading: resetting}] =
    useResetRawTransactionMutation({
      refetchQueries: ['RetrieveRawTransaction'],
    });
  const consumerCount = rawTransaction.consumers.length;

  // Unallocating deletes every consuming transaction and releases every other
  // line they consume, so the header button only arms it: the confirm row
  // names what goes before anything happens.
  const [confirmingReset, setConfirmingReset] = React.useState(false);
  const otherLineCount = new Set(
    rawTransaction.consumers.flatMap((c) =>
      c.transaction.consumptions
        .map((consumption) => consumption.rawTransactionId)
        .filter((id) => id !== rawTransaction._id),
    ),
  ).size;
  const consumerNames = rawTransaction.consumers.map(
    (c) =>
      c.transaction.payee ||
      (c.transaction.transferType
        ? TRANSFER_TYPE_LABELS[c.transaction.transferType]
        : 'Transfer'),
  );
  const handleReset = () => {
    setConfirmingReset(false);
    void resetRawTransaction({
      variables: {rawTransactionId: rawTransaction._id},
    });
  };

  const handleEdit = () => {
    // The modal owns the cache bookkeeping for account moves and benefit
    // changes, so there is nothing to clean up here.
    modals.editRawTransactionModal.openModal({rawTransaction});
  };

  const handleDelete = () => {
    modals.deleteRawTransactionModal.openModal({
      rawTransactions: [rawTransaction],
      onDelete: () => {
        // The account's cached list pages still contain the deleted row
        evictAccountRawTransactions(
          apolloClient.cache,
          rawTransaction.account._id,
        );
        onDelete?.();
      },
    });
  };

  const sourceData = (
    [
      ['Plaid', rawTransaction.plaidData],
      ['Plaid investment', rawTransaction.plaidInvestmentTransactionData],
      ['EZPass', rawTransaction.ezpassData],
    ] as const
  ).flatMap(([label, json]) => (json != null ? [{label, json}] : []));

  return (
    <Container>
      <DetailHeader
        title={rawTransaction.description || 'Activity'}
        amount={
          <AmountDisplay
            align="left"
            amount={Money.fromInt(
              rawTransaction.amount,
              rawTransaction.currency,
            )}
          />
        }
        actions={
          <>
            {/* Always offered — the edit form locks the fields sync owns
                (date/description/amount on Plaid/Venmo rows) individually,
                while account and benefit stay editable on any row. */}
            <Button variant="outline" onClick={handleEdit}>
              Edit
            </Button>
            {consumerCount > 0 && (
              // Unallocating deletes every consuming transaction, so it
              // takes a second press to confirm.
              <Button
                variant="outline"
                className={destructiveOutlineClass}
                disabled={resetting || confirmingReset}
                onClick={() => setConfirmingReset(true)}
              >
                Unallocate
              </Button>
            )}
            {deleteBlockedReason === null && (
              <Button
                variant="outline"
                className={destructiveOutlineClass}
                onClick={handleDelete}
              >
                Delete
              </Button>
            )}
          </>
        }
      />
      <Section header="Details">
        <Display rawTransaction={rawTransaction} />
      </Section>
      <Section header={consumerCount === 1 ? 'Transaction' : 'Transactions'}>
        <ConsumersList
          consumers={rawTransaction.consumers}
          currency={rawTransaction.currency}
          totalAmount={rawTransaction.amount}
          remainingAmount={rawTransaction.remainingAmount}
        />
        {confirmingReset && (
          <div className="border-border flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3">
            <div className="text-sm">
              Delete {consumerNames.join(', ')}
              {otherLineCount > 0 &&
                ` and release ${otherLineCount} other line${otherLineCount === 1 ? '' : 's'}`}
              ?
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setConfirmingReset(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={resetting}
                onClick={handleReset}
              >
                Unallocate
              </Button>
            </div>
          </div>
        )}
      </Section>
      {sourceData.length > 0 && (
        <details open className="group flex flex-col gap-2">
          <summary className="flex cursor-pointer list-none items-center gap-1 text-lg select-none">
            <ChevronRight className="text-muted-foreground size-4 transition-transform group-open:rotate-90" />
            Source data
          </summary>
          <div className="flex flex-col gap-4 pt-2">
            {sourceData.map(({label, json}) => (
              <Section key={label} header={label}>
                <JsonDisplay json={json} />
              </Section>
            ))}
          </div>
        </details>
      )}
    </Container>
  );
};

export default RawTransactionDetail;
